<template>
    <div>
        <section class="section">
            <div class="container is-fluid">
                <h3 class="title">{{ $t("jobPreview.title", { jobName: job.name }) }}</h3>
                <hr />

                <section v-for="unit in this.units" :key="unit.id">
                    <h4 class="title is-2">
                        {{ $t("jobPreview.unitName", { name: formatUnitName(unit) }) }}
                    </h4>

                    <div v-for="date in unit.chunksIDs" :key="unit.id + date">
                        <span class="tag is-primary date-tag m-b-margin">
                            <h5 class="subtitle is-6 has-text-white">{{ date }}</h5>
                        </span>

                        <div class="trip-list">
                            <div v-for="(trip, index) in unit.chunkedRows[date]" :key="index" class="trip-card">
                                <div class="trip-header">
                                    <span class="trip-id-tag tag is-primary">#{{ index + 1 }}</span>
                                    <span class="trip-type">
                                        <b-select v-model="trip.status" placeholder="Private/Personal">
                                            <option v-for="option in tripStatusOptions" :value="option.name"
                                                :key="option.name">
                                                {{ option.label }}
                                            </option>
                                        </b-select>
                                    </span>
                                </div>

                                <div class="trip-details">
                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.startTime") }}:</span>
                                        <span>{{ trip.beginning }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.endTime") }}:</span>
                                        <span>{{ trip.end }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.duration") }}:</span>
                                        <span>{{ trip.duration }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.fromLocation") }}:</span>
                                        <LocationCell :location="trip.fromLocation" />
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.initialMileage") }}:</span>
                                        <span>{{ isNaN(trip.initialMileage) ? trip.initialMileage :
                    trip.initialMileage.toFixed(2) }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.toLocation") }}:</span>
                                        <LocationCell :location="trip.toLocation" />
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.finalMileage") }}:</span>
                                        <span>{{ isNaN(trip.finalMileage) ? trip.finalMileage :
                    trip.finalMileage.toFixed(2) }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.mileage") }}:</span>
                                        <span>{{ trip.mileage.toFixed(2) }}</span>
                                    </div>

                                    <div class="detail-item">
                                        <span class="label no-b-margin">{{ $t("misc.driver") }}:</span>
                                        <span>{{ trip.driver }}</span>
                                    </div>
                                </div>

                                <div class="trip-notes">
                                    <span class="label no-b-margin">{{ $t("misc.notes") }}:</span>
                                    <b-input v-model="trip.notes" type="textarea" rows="2" maxlength="192"></b-input>
                                </div>
                            </div>
                        </div>
                        <br />
                    </div>
                </section>

                <div class="print-button-container" v-if="downloadButtonVisible">
                    <b-dropdown aria-role="list" position="is-top-left">
                        <template #trigger="{ active }">
                            <b-button type="is-primary print-button" :icon-right="active ? 'menu-up' : 'menu-down'">
                                <span class="mdi mdi-download"></span>
                                {{ $t("misc.download") }}
                            </b-button>
                        </template>

                        <b-dropdown-item aria-role="listitem" class="print-dropdown-item"
                            @click="() => createReport('pdf')">
                            <b-icon icon="file-pdf-box" size="is-small" /> PDF
                        </b-dropdown-item>
                        <b-dropdown-item aria-role="listitem" class="print-dropdown-item"
                            @click="() => createReport('excel')">
                            <b-icon icon="file-excel-box" size="is-small" /> Excel
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
        </section>

        <b-loading :is-full-page="true" v-model="isLoading"></b-loading>
    </div>
</template>

<script>

const axios = require('axios');

import errorCodes from "@/assets/errors.js"
import LocationCell from "@/components/misc/LocationCell.vue"

import { formatUnitName } from "@/utils/format";

export default {
    name: "JobPreview",
    components: {
        LocationCell
    },
    data: function () {
        return {
            isLoading: false,
            job: {
                name: "",
                mailto: "",
                units: [],
                frequency: ""
            },
            api: axios.create({
                baseURL: process.env.VUE_APP_JOBS_API_ROOT,
                headers: { Authorization: `Bearer ${localStorage.backendToken}` }
            }),
            units: [],
            tripStatusOptions: [
                {
                    name: "Business",
                    label: this.$t("misc.business")
                },
                {
                    name: "Personal",
                    label: this.$t("misc.private")
                }
            ],
            downloadButtonVisible: false
        }
    },
    methods: {
        formatUnitName: formatUnitName,
        fetchReportData() {
            let view = this;
            view.isLoading = true;

            if ("id" in this.job) {
                // Execute an existing job
                this.api.get("job-data/" + this.job.id)
                    .then(this.handleReportResponse)
                    .catch(this.handleReportErrors);
            } else {
                // Generate an on-the-fly report
                let payload = {
                    units: this.job.units,
                    startDate: this.job.startDate,
                    endDate: this.job.endDate
                }

                this.api.post("generate-report", payload)
                    .then(this.handleReportResponse)
                    .catch(this.handleReportErrors);
            }
        },
        handleReportResponse(response) {
            let units = response.data.units;

            units.forEach(unit => {
                // Add notes field
                unit.rows.forEach(row => {
                    row.notes = "";
                });

                unit.chunkedRows = this.chunkTripsInDays(unit.rows);
                unit.chunksIDs = Object.keys(unit.chunkedRows);
            });

            this.units = units;
            this.downloadButtonVisible = true;
            this.isLoading = false;
        },
        handleReportErrors(error) {
            let response = error.response;
            this.isLoading = false;

            if (response.status == 401) {
                this.$buefy.toast.open({
                    message: this.$t("jobPreview.loadingError"),
                    type: 'is-danger',
                    duration: 3000
                })
            } else if (response.status == 400 && response.data.code == errorCodes.unitMissingSensors) {
                this.$buefy.toast.open({
                    message: this.$t("jobPreview.missingUnitSensor"),
                    type: 'is-danger',
                    duration: 3000
                })
            } else if (response.status == 404 && response.data.code == errorCodes.unitMissingReport) {
                this.$buefy.toast.open({
                    message: this.$t("jobPreview.missingReport"),
                    type: 'is-danger',
                    duration: 3000
                })
            }
        },
        chunkTripsInDays(trips) {
            var datesDictionary = {}

            trips.forEach(row => {
                let tripStartDay = row.beginning.split(" ")[0];

                if (!(tripStartDay in datesDictionary)) {
                    datesDictionary[tripStartDay] = []
                }

                datesDictionary[tripStartDay].push(row);
            });

            return datesDictionary
        },
        createReport(format) {
            // Transfer the chunked trips to the rows array and remove
            // the chunked data. A deep copy is used here to prevent issues
            // with the UI 
            let clonedUnits = JSON.parse(JSON.stringify(this.units));

            clonedUnits.forEach(function (unit, index) {
                var processedUnitRows = [];
                let unitDates = Object.keys(unit.chunkedRows);

                unitDates.sort();
                unitDates.reverse();

                unitDates.forEach(date => {
                    processedUnitRows = processedUnitRows.concat(unit.chunkedRows[date]);
                });

                unit.rows = processedUnitRows.map((row) => {
                    return {
                        ...row,
                        status: row.status ? row.status.toLowerCase() : "---"
                    }
                });

                delete unit.chunkedRows;

                clonedUnits[index] = unit;
            });

            // Send the PDF generation request
            let view = this;

            this.api.post(`create-report-${format}`, clonedUnits, { responseType: 'blob' }).then(function (response) {
                const mimeType = {
                    pdf: "application/pdf",
                    excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                }[format]

                // Create a PDF/Excel file
                const file = new Blob([response.data], { type: mimeType });
                const fileURL = URL.createObjectURL(file);

                // Open the URL in a new Window
                window.open(fileURL);
            }).catch(error => {
                if (error.response.status == 401) {
                    view.$buefy.toast.open({
                        message: view.$t("jobPreview.pdfError"),
                        type: 'is-danger',
                        duration: 3000
                    })

                    return;
                }
            });
        }
    },
    mounted: function () {
        this.job = this.$route.params.job;
        this.fetchReportData();
    }
};

</script>

<style scoped>
.date-tag {
    padding: 16px 12px 18px 12px;
}

.print-button-container {
    position: fixed;
    bottom: 32px;
    right: 32px;
}

.print-button {
    box-shadow: 0px 3px 8px 2px rgba(0, 0, 0, 0.2) !important;
    font-size: 20px;
}

.print-dropdown-item {
    font-size: 16px;
}

.container {
    max-width: 1680px !important;
}

.notes-column {
    width: 256px;
}

.trip-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.trip-card {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 1rem;
    background-color: #fff;
    padding-bottom: 0rem;
}

.trip-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.trip-id-tag {
    font-size: 1rem;
    height: 2.5rem;
    border-radius: 1.25rem;
}

.trip-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.5rem;
}

.detail-item {
    display: flex;
    flex-direction: column;
}

.trip-notes {
    margin-top: 1rem;
}

@media (max-width: 768px) {
    .trip-details {
        grid-template-columns: 1fr;
    }
}
</style>